/**
 * ManagerView — Direction A "CRM Operator" UI.
 *
 * Renders the Mi6 black + white CRM-operator chrome (OperatorShell) with the
 * pilot manager dashboard inside. Wires to the same Supabase data hooks the
 * legacy view used (preserved at ManagerView.legacy.tsx.bak).
 *
 * Reference mockup: /home/user/workspace/aip-ui-direction-a-concept.png
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { supabase, getSession, getCurrentTenant, api } from "@/lib/session";
import { friendlyError } from "@/lib/friendly-error";
import { buildDrillUrl } from "@/lib/drillUrl";
import OperatorShell from "@/components/operator/OperatorShell";
import OperatorKpiCard from "@/components/operator/OperatorKpiCard";
import OperatorTable, { StagePill, HelixBar, OwnerPill, type OperatorColumn } from "@/components/operator/OperatorTable";
import OperatorRightRail, { RailCard, PipelineByStage, ActivityFeed } from "@/components/operator/OperatorRightRail";
import ConversionRateBand from "@/components/dashboards/ConversionRateBand";
import { tokens } from "@/components/operator/tokens";
import { TimingBadge, SoftStopBadge, ReEntryBadge, ExtConsultantBadge, ForecastCategoryPill } from "@/components/dashboards/DealBadges";
import { resolveCategory, blendedForecastPct, type DeclaredTiming } from "@/lib/forecast-rules";
import ManagerReviewQueue from "@/components/dashboards/ManagerReviewQueue";
import CoachingSurface from "@/components/manager/CoachingSurface";

// ─── Types (Supabase row shapes — copied from legacy) ───────────────────────

type UserRow = {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  tenant_id: number;
};

type AccountRow = {
  id: number;
  tenant_id: number;
  owner_user_id: string;
  name: string;
  industry: string | null;
  status: string | null;
  aip_ext: any;
  updated_at: string;
};

type CpStatusRow = {
  account_id: number;
  cp_number: number;
  status: string | null;
  is_manual: boolean;
  updated_at: string;
};

type AnswerRow = {
  account_id: number;
  question_key: string;
  selected_option: string | null;
  evidence: string;
  updated_at: string;
};

type SalesPlanRow = {
  ae_user_id: string;
  plan_year: number;
  annual_quota: number;
};

type PipelineSummaryRow = {
  ae_user_id: string;
  weighted_pipeline: number | null;
  won_value: number | null;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtMoney(n: number): string {
  if (!isFinite(n) || n === 0) return "$0";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function estArrFor(a: AccountRow): number {
  const v = a?.aip_ext?.est_arr;
  return typeof v === "number" ? v : Number(v) || 0;
}

// Map server bucket / stage / cp counts to a Direction A stage label.
function stageFor(account: AccountRow, cps: CpStatusRow[]): string {
  const ext = account?.aip_ext ?? {};
  const explicit = (account.status || ext.bucket || "").toString().toLowerCase();
  if (explicit.includes("won") || explicit === "commit") return "Closing";
  if (explicit === "probable") return "Proposal";
  if (explicit === "upside") return "Evaluating";
  if (explicit === "omitted" || explicit === "stalled") return "Stalled";
  const reds = (cps ?? []).filter((c) => c.status === "red").length;
  const greens = (cps ?? []).filter((c) => c.status === "green").length;
  if (reds >= 2) return "Stalled";
  if (greens >= 9) return "Closing";
  if (greens >= 7) return "Proposal";
  if (greens >= 5) return "Evaluating";
  return "Prospecting";
}

function helixFor(cps: CpStatusRow[]): number {
  // Track D rebuild patch (2026-05-14): removed hardcoded fallback of 30 for accounts
  // with no CP data. Real tenants with unscored accounts should not be assigned
  // an invented score. Returns 0 when no CPs exist — renders as dashes in the UI
  // via the avgHelix guard below.
  if (!cps?.length) return 0;
  let pts = 0;
  for (const c of cps) {
    if (c.status === "green") pts += 10;
    else if (c.status === "amber") pts += 5;
  }
  return Math.min(100, Math.round((pts / 100) * 100));
}

function ownerInitial(name?: string): string {
  if (!name) return "—";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0][0]!.toUpperCase();
  return (parts[0][0] + ". " + parts[parts.length - 1]).slice(0, 16);
}

// ─── Decorated row type ─────────────────────────────────────────────────────

interface DecoratedAccount {
  id: number;
  name: string;
  industry: string;
  employees: string;
  stage: string;
  helix: number;
  arr: number;
  ownerName: string;
  timing: DeclaredTiming;
  softStops: number;
  reEntry?: boolean;
  extConsultant?: boolean;
  raw: AccountRow;
}

// ─── Quarter label ──────────────────────────────────────────────────────────

function quarterLabel(): string {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}

// ─── Demo seed — tenant 2 (Mi6 Demo) only. Real tenants get an empty state. —

const DEMO_ACCOUNTS: DecoratedAccount[] = [
  { id: 1, name: "Meridian Metals",    industry: "Metals & Mining", employees: "420 emp", stage: "Closing",    helix: 82, arr: 180000, ownerName: "J. Torres", timing: 90, softStops: 0, raw: null as any },
  { id: 2, name: "Vertex Energy",      industry: "Energy / Utilities", employees: "210 emp", stage: "Evaluating", helix: 64, arr: 95000,  ownerName: "A. Patel",  timing: 70, softStops: 1, reEntry: true, raw: null as any },
  { id: 3, name: "Cascade Mfg",        industry: "Industrial Manufacturing", employees: "850 emp", stage: "Proposal",   helix: 71, arr: 240000, ownerName: "B. Short",  timing: 80, softStops: 0, raw: null as any },
  { id: 4, name: "Northgate Aerospace",industry: "Aerospace & Defense", employees: "1,200 emp", stage: "Stalled",  helix: 38, arr: 310000, ownerName: "M. Chen",   timing: 50, softStops: 3, raw: null as any },
  { id: 5, name: "Summit Consumer Goods",industry: "Consumer Goods Manufacturing", employees: "560 emp", stage: "Evaluating", helix: 55, arr: 72000,  ownerName: "J. Torres", timing: 60, softStops: 2, extConsultant: true, raw: null as any },
];

// Canon palette — no navy/blue/slate.
const DEMO_PIPELINE = [
  { stage: "Prospecting", count: 5, color: "#EA580C" }, // orange (Pipeline)
  { stage: "Evaluating",  count: 3, color: "#EAB308" }, // yellow (Best Case)
  { stage: "Proposal",    count: 2, color: "#16A34A" }, // green (Upside)
  { stage: "Closing",     count: 1, color: "#16A34A" }, // green (Commit)
  { stage: "Stalled",     count: 3, color: "#DC2626" }, // red (Omit)
];

const DEMO_ACTIVITY = [
  { text: "Meridian Metals moved to Closing stage", sub: "J. Torres", time: "42 min ago", color: tokens.color.success },
  { text: "Northgate Aerospace Helix score dropped to 38", sub: "System",   time: "2 hr ago",  color: tokens.color.danger  },
  { text: "Cascade Mfg proposal sent — awaiting response", sub: "B. Short", time: "3 hr ago", color: tokens.color.info },
  { text: "Vertex Capital CP review scheduled", sub: "A. Patel", time: "Yesterday", color: tokens.color.textSubtle },
];

// ─── Main view ──────────────────────────────────────────────────────────────

export default function ManagerView() {
  const session = getSession();
  const TENANT_ID = session?.tenantId ?? 2;
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;
  const [, setLocation] = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [cps, setCps] = useState<CpStatusRow[]>([]);
  const [answers, setAnswers] = useState<AnswerRow[]>([]);
  const [plans, setPlans] = useState<SalesPlanRow[]>([]);
  const [pipeline, setPipeline] = useState<PipelineSummaryRow[]>([]);
  const [updated, setUpdated] = useState<Date>(new Date());
  // Map of account_id -> composite score from /api/manager/portfolio (same source as AccountScorecard).
  const [compositeMap, setCompositeMap] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const sb = supabase();
        const year = new Date().getFullYear();

        const { data: usersData, error: usersErr } = await sb
          .from("users")
          .select("id, email, full_name, role, tenant_id")
          .eq("tenant_id", TENANT_ID)
          .in("role", ["ae", "bdr"]);
        if (usersErr) throw usersErr;
        const teamUsers = (usersData ?? []) as UserRow[];
        const aeIds = teamUsers.filter((u) => u.role === "ae").map((u) => u.id);

        const { data: accData, error: accErr } = aeIds.length
          ? await sb
              .from("accounts")
              .select("id, tenant_id, owner_user_id, name, industry, status, aip_ext, updated_at")
              .eq("tenant_id", TENANT_ID)
              .in("owner_user_id", aeIds)
          : { data: [], error: null };
        if (accErr) throw accErr;
        const accs = (accData ?? []) as AccountRow[];
        const accIds = accs.map((a) => a.id);

        const { data: cpData } = accIds.length
          ? await sb.from("account_cp_status").select("account_id, cp_number, status, is_manual, updated_at").in("account_id", accIds)
          : { data: [] };
        const { data: ansData } = accIds.length
          ? await sb.from("account_answers").select("account_id, question_key, selected_option, evidence, updated_at").in("account_id", accIds).order("updated_at", { ascending: false }).limit(10)
          : { data: [] };
        const { data: planData } = aeIds.length
          ? await sb.from("sales_plans").select("ae_user_id, plan_year, annual_quota").eq("tenant_id", TENANT_ID).eq("plan_year", year).in("ae_user_id", aeIds)
          : { data: [] };
        const { data: pipeData } = aeIds.length
          ? await sb.from("v_ae_pipeline_summary").select("ae_user_id, weighted_pipeline, won_value").eq("tenant_id", TENANT_ID).in("ae_user_id", aeIds)
          : { data: [] };

        if (cancelled) return;
        setUsers(teamUsers);
        setAccounts(accs);
        setCps((cpData ?? []) as CpStatusRow[]);
        setAnswers((ansData ?? []) as AnswerRow[]);
        setPlans((planData ?? []) as SalesPlanRow[]);
        setPipeline((pipeData ?? []) as PipelineSummaryRow[]);
        setUpdated(new Date());

        // Fetch the canonical composite scores in parallel so the HELIX column
        // matches the AccountScorecard composite. Failures are non-fatal — we
        // fall back to the legacy CP-rollup helixFor() formula below.
        try {
          const portfolio: any = await api("/api/manager/portfolio");
          // /api/manager/portfolio returns a raw array of accounts (not wrapped).
          const accts: any[] = Array.isArray(portfolio)
            ? portfolio
            : Array.isArray(portfolio?.accounts) ? portfolio.accounts : [];
          if (!cancelled) {
            const m = new Map<number, number>();
            for (const a of accts) {
              if (typeof a?.id === "number" && typeof a?.score === "number" && a.score > 0) {
                m.set(a.id, Math.round(a.score));
              }
            }
            setCompositeMap(m);
          }
        } catch {
          // ignore — helixFor fallback is fine
        }
      } catch (e: any) {
        if (!cancelled) setError(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [TENANT_ID]);

  // ── Decorate accounts ────────────────────────────────────────────────────
  const userById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const cpByAccount = useMemo(() => {
    const m = new Map<number, CpStatusRow[]>();
    for (const c of cps) {
      if (!m.has(c.account_id)) m.set(c.account_id, []);
      m.get(c.account_id)!.push(c);
    }
    return m;
  }, [cps]);

  const liveDecorated: DecoratedAccount[] = useMemo(() => {
    return accounts.map((a) => {
      const accCps = cpByAccount.get(a.id) ?? [];
      const owner = userById.get(a.owner_user_id);
      const ownerName = owner?.full_name || owner?.email?.split("@")[0] || "—";
      const employees = a.aip_ext?.employees || a.aip_ext?.headcount;
      const ext = a.aip_ext ?? {};
      const tRaw = Number(ext.declared_timing);
      const t: DeclaredTiming =
        tRaw === 90 || tRaw === 80 || tRaw === 70 || tRaw === 60 || tRaw === 50 || tRaw === 0
          ? (tRaw as DeclaredTiming)
          : 70;
      return {
        id: a.id,
        name: a.name,
        industry: a.industry || "—",
        employees: employees ? `${employees} emp` : "—",
        stage: stageFor(a, accCps),
        helix: compositeMap.get(a.id) ?? helixFor(accCps),
        arr: estArrFor(a),
        ownerName: ownerInitial(ownerName),
        timing: t,
        softStops: Number(ext.soft_stop_count ?? 0),
        reEntry: ext.push_re_entry === true,
        extConsultant: ext.ext_consultant === true,
        raw: a,
      };
    }).sort((x, y) => y.arr - x.arr);
  }, [accounts, cpByAccount, userById, compositeMap]);

  const usingDemo = liveDecorated.length === 0 && isDemo;
  const decorated = liveDecorated.length > 0 ? liveDecorated : (isDemo ? DEMO_ACCOUNTS : []);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const teamBooked = pipeline.reduce((s, p) => s + Number(p.won_value || 0), 0);
  const teamWeighted = pipeline.reduce((s, p) => s + Number(p.weighted_pipeline || 0), 0);
  const liveArr = teamBooked + teamWeighted;

  // D-048: when v_ae_pipeline_summary returns no data (fresh tenant or view not yet
  // populated), fall back to summing estArr of all non-terminal accounts so Pipeline
  // ARR is never incorrectly shown as $0 when real accounts exist.
  const accountBasedArr = accounts
    .filter((a) => !(a.status === "Won" || a.status === "Lost"))
    .reduce((s, a) => s + estArrFor(a), 0);
  const pipelineArr = usingDemo ? 2_400_000 : (liveArr > 0 ? liveArr : accountBasedArr);
  // Track D rebuild (2026-05-14): avgHelix now excludes accounts with helix=0 (unscored).
  // Prevents a zero-dragged average misrepresenting genuinely-scored accounts.
  const scoredAccounts = decorated.filter((d) => d.helix > 0);
  const avgHelix = scoredAccounts.length > 0
    ? Math.round(scoredAccounts.reduce((s, a) => s + a.helix, 0) / scoredAccounts.length)
    : 0;
  const passRate = usingDemo ? 58 : Math.min(99, Math.round((decorated.filter((d) => d.helix >= 65).length / Math.max(1, decorated.length)) * 100));
  const dealsAtRisk = decorated.filter((d) => d.stage === "Stalled" || d.helix < 45).length;

  // Pipeline-by-stage rail
  const pipelineByStage = useMemo(() => {
    if (usingDemo) return DEMO_PIPELINE;
    const counts = new Map<string, number>();
    for (const d of decorated) counts.set(d.stage, (counts.get(d.stage) ?? 0) + 1);
    const order = ["Prospecting", "Evaluating", "Proposal", "Closing", "Stalled"];
    return order.map((s) => ({
      stage: s,
      count: counts.get(s) ?? 0,
      color: s === "Closing" ? tokens.color.success : s === "Stalled" ? tokens.color.danger : tokens.color.black,
    }));
  }, [decorated, usingDemo]);

  // Activity feed rail
  const activityItems = useMemo(() => {
    if (usingDemo) return DEMO_ACTIVITY;
    return answers.slice(0, 6).map((a) => {
      const acc = accounts.find((x) => x.id === a.account_id);
      const ageMs = Date.now() - new Date(a.updated_at).getTime();
      const ageMin = Math.max(1, Math.floor(ageMs / 60_000));
      const ago = ageMin < 60 ? `${ageMin} min ago` : ageMin < 1440 ? `${Math.floor(ageMin / 60)} hr ago` : `${Math.floor(ageMin / 1440)} d ago`;
      return {
        text: `${acc?.name || `Account ${a.account_id}`} · ${a.question_key}${a.selected_option ? ` → ${a.selected_option}` : ""}`,
        sub: a.evidence ? `"${a.evidence.slice(0, 60)}"` : undefined,
        time: ago,
        color: tokens.color.black,
      };
    });
  }, [answers, accounts, usingDemo]);

  // ── Columns for the operator table ───────────────────────────────────────
  const columns: OperatorColumn<DecoratedAccount>[] = [
    {
      key: "name",
      header: "Account",
      render: (d) => (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, color: tokens.color.textPrimary, fontSize: 14 }}>{d.name}</span>
            {d.reEntry && <ReEntryBadge />}
            {d.extConsultant && <ExtConsultantBadge />}
          </div>
          <div style={{ fontSize: 12, color: tokens.color.textMuted, marginTop: 2 }}>
            {d.industry} · {d.employees}
          </div>
        </div>
      ),
    },
    { key: "stage", header: "Stage", render: (d) => <StagePill stage={d.stage} /> },
    { key: "timing", header: "Timing", render: (d) => <TimingBadge timing={d.timing} /> },
    {
      key: "forecast", header: "Forecast",
      render: (d) => (
        <div style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          <ForecastCategoryPill category={resolveCategory(d.helix)} timing={d.timing} />
          <span style={{ fontSize: 12, color: tokens.color.textMuted, fontVariantNumeric: "tabular-nums" }}>
            {d.helix}% × {d.timing === 0 ? "<50" : `${d.timing}`}% = {blendedForecastPct(d.helix, d.timing)}%
          </span>
        </div>
      ),
    },
    { key: "helix", header: "Helix", render: (d) => <HelixBar score={d.helix} /> },
    { key: "softstop", header: "Flags", render: (d) => <SoftStopBadge count={d.softStops} /> },
    {
      key: "arr",
      header: "ARR",
      numeric: true,
      render: (d) => (
        <span style={{ fontWeight: 600, color: tokens.color.textPrimary }}>{fmtMoney(d.arr)}</span>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      render: (d) => <OwnerPill name={d.ownerName} />,
    },
  ];

  const updatedAgo = (() => {
    const m = Math.max(1, Math.floor((Date.now() - updated.getTime()) / 60_000));
    return m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} hr ago`;
  })();

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Accounts", href: "/accounts" },
        { label: "Manager View" },
      ]}
      primaryAction={{ label: "+ Add Account", href: "/accounts/new" }}
      pageTitle="Manager view"
      pageMeta="Per-rep team activity, deals in motion, and coaching triggers."
    >
      <ConversionRateBand scope="team" role="exec" />
      {/* KPI row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 10,
          marginBottom: 10,
        }}
      >
        {/* Track D patch: "12% vs last quarter" was a hardcoded string literal. Replaced with honest empty delta. */}
        {/* BUILD-14: KPI cards now navigate to filtered pipeline on click */}
        <div onClick={() => setLocation(buildDrillUrl('/pipeline', { from: '/dashboards/manager', fromLabel: 'Manager Dashboard' }))} style={{ cursor: 'pointer' }} title="Click to view full team pipeline">
        <OperatorKpiCard
          label="Pipeline ARR"
          value={fmtMoney(pipelineArr)}
          delta={usingDemo ? { value: "12% vs last quarter", direction: "up", tone: "good" } : (pipelineArr > 0 ? { value: "Active pipeline — click to view", direction: "up", tone: "good" } : { value: "Data activates as your team logs activity", direction: "flat", tone: "neutral" })}
        />
        </div>
        <OperatorKpiCard
          label="Avg Helix Score"
          value={String(avgHelix > 0 ? avgHelix : (usingDemo ? 71 : "—"))}
          delta={usingDemo ? { value: "4 pts this month", direction: "up", tone: "good" } : (avgHelix > 0 ? { value: "Based on current Helix coverage", direction: "flat", tone: "neutral" } : { value: "Data activates as your team logs activity", direction: "flat", tone: "neutral" })}
        />
        <OperatorKpiCard
          label="CP Pass Rate"
          value={`${passRate}%`}
          delta={usingDemo ? { value: "3% vs target", direction: "down", tone: "bad" } : { value: "Based on Helix scores", direction: "flat", tone: "neutral" }}
        />
        <div onClick={() => setLocation(buildDrillUrl('/pipeline', { stage: 'Stalled', from: '/dashboards/manager', fromLabel: 'Manager Dashboard' }))} style={{ cursor: 'pointer' }} title="Click to view stalled deals">
        <OperatorKpiCard
          label="Deals at Risk"
          value={String(dealsAtRisk)}
          delta={dealsAtRisk > 0 ? { value: "Click to view stalled deals", direction: "down", tone: "bad" } : (usingDemo ? { value: "Click to view stalled deals", direction: "down", tone: "bad" } : { value: "No stalled deals detected", direction: "up", tone: "good" })}
        />
        </div>
      </div>

      {/* Body: account list + right rail */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <OperatorTable
            title="Account List"
            columns={columns}
            rows={decorated}
            rowKey={(d) => d.id}
            onRowClick={(d) => setLocation(`/account/${d.id}`)}
            emptyMessage={loading ? "Loading accounts..." : error ? `Could not load accounts: ${error}` : "No accounts yet."}
          />
        </div>

        <OperatorRightRail>
          {/* W18: Coaching triggers surface */}
          <RailCard title="Coaching triggers">
            <CoachingSurface tenantId={TENANT_ID} />
          </RailCard>
          <RailCard title="Review queue" footer="PUSH adequacy · Blackout overrides · Soft-stop escalations">
            <ManagerReviewQueue tenantId={TENANT_ID} />
          </RailCard>
          {/* BUILD-14: stage bars drill into filtered pipeline */}
          <RailCard title="Pipeline by Stage">
            <PipelineByStage
              rows={pipelineByStage}
              onRowClick={(stage) =>
                setLocation(buildDrillUrl('/pipeline', { stage, from: '/dashboards/manager', fromLabel: 'Manager Dashboard' }))
              }
            />
          </RailCard>
          <RailCard title="Recent Activity">
            <ActivityFeed items={activityItems} />
          </RailCard>
        </OperatorRightRail>
      </div>
    </OperatorShell>
  );
}
